import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTrash2, FiEdit2, FiCheck, FiCopy, FiFile, FiFolder, FiSettings, FiMenu } = FiIcons;

const PageItem = ({ page, isActive, isHome, pages, onSelect, onRename, onDuplicate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(page.name);
  const [menuOpen, setMenuOpen] = useState(false);

  // Only the home page is completely protected from deletion/renaming
  const isSystem = page.id === 'page-home';
  
  const dragControls = useDragControls();

  // Close context menu if clicked outside
  useEffect(() => {
    const handleClick = () => setMenuOpen(false);
    if (menuOpen) window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [menuOpen]);

  const commitRename = () => {
    if (name.trim()) onRename(page.id, name.trim());
    setEditing(false);
  };

  return (
    <Reorder.Item
      value={page}
      dragListener={false}
      dragControls={dragControls}
      className="relative bg-[#f8f9fa]" // ensures solid background when dragging
    >
      <div
        onClick={() => !editing && onSelect(page.id)}
        className={`group flex items-center justify-between px-6 py-3.5 cursor-pointer transition-all border-b border-gray-200/60 ${
          isActive ? 'bg-white shadow-[0_2px_10px_rgba(0,0,0,0.03)]' : 'hover:bg-white/40'
        }`}
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <div
            className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 p-1 -ml-2"
            onPointerDown={(e) => {
              e.stopPropagation();
              dragControls.start(e);
            }}
          >
            <SafeIcon icon={FiMenu} className="w-[18px] h-[18px]" />
          </div>
          <SafeIcon icon={FiFile} className={`w-4 h-4 shrink-0 ${isActive ? 'text-gray-900' : 'text-gray-600'}`} />
          {editing ? (
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') { setEditing(false); setName(page.name); }
              }}
              onClick={e => e.stopPropagation()}
              className="w-full text-sm font-medium bg-white border border-[#5494d4] rounded px-2 py-0.5 outline-none shadow-sm ml-1"
            />
          ) : (
            <span className={`text-[15px] truncate ml-1 ${isActive ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
              {page.name}
            </span>
          )}
        </div>

        {!editing && (
          <div className="flex items-center space-x-1">
            {!isSystem && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(page.id);
                  setMenuOpen(false);
                }}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all opacity-0 group-hover:opacity-100"
                title="Delete Page"
              >
                <SafeIcon icon={FiTrash2} className="w-[16px] h-[16px]" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <SafeIcon icon={FiSettings} className="w-[18px] h-[18px]" />
            </button>
          </div>
        )}
      </div>

      {/* Dropdown Actions */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute right-6 top-10 w-36 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden"
          >
            {!isSystem && (
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(true); setMenuOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <SafeIcon icon={FiEdit2} className="w-4 h-4 text-gray-400" />
                <span>Rename</span>
              </button>
            )}
            
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(page.id); setMenuOpen(false); }}
              className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4 text-gray-400" />
              <span>Duplicate</span>
            </button>
            
            {!isSystem && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(page.id); setMenuOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center space-x-2"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-400" />
                <span>Delete</span>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Reorder.Item>
  );
};

const PagesPanel = ({ isOpen, pages, activePageId, onAddPage, onSelectPage, onDeletePage, onRenamePage, onDuplicatePage, onReorderPages }) => {
  const [activeTab, setActiveTab] = useState('Pages');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="bg-[#f8f9fa] border-r border-gray-200 h-full w-full flex flex-col"
        >
          {/* Header Area */}
          <div className="pt-8 pb-5 flex flex-col items-center border-b border-gray-200 bg-white shrink-0">
            <h2 className="text-[22px] font-medium text-gray-800 mb-5">Pages & Popups</h2>
            
            <button
              onClick={() => onAddPage('New Page')}
              className="bg-[#5494d4] hover:bg-blue-600 text-white px-6 py-2.5 rounded-full font-medium text-[15px] flex items-center space-x-2 transition-colors shadow-sm mb-4"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add New...</span>
            </button>

            <div className="flex items-center space-x-2 text-[13px]">
              <span className="text-gray-600 font-medium">Need Help?</span>
              <a href="#" className="text-[#e11d48] font-medium flex items-center space-x-1.5 hover:text-red-700 transition-colors">
                <span>Play Video</span>
                <div className="bg-[#e11d48] text-white rounded-full w-[18px] h-[18px] flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-2.5 h-2.5 translate-x-[1px]">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </a>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center justify-center space-x-8 border-b border-gray-200 bg-white px-6 shrink-0">
            {['Pages', 'Popups'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 text-[15px] font-medium border-b-2 transition-colors ${
                  activeTab === tab ? 'text-gray-900 border-[#5494d4]' : 'text-gray-400 border-transparent hover:text-gray-600'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* List Area */}
          <div className="flex-1 overflow-y-auto pt-6 pb-4 custom-scrollbar bg-[#f8f9fa]">
            {activeTab === 'Pages' ? (
              <>
                <div className="flex items-center space-x-3 mb-2 px-6">
                  <div className="w-10 h-10 bg-[#e8ebf0] rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiFolder} className="w-[18px] h-[18px] text-gray-700" />
                  </div>
                  <span className="text-[15px] font-medium text-gray-800">PAGES</span>
                </div>
                
                <Reorder.Group axis="y" values={pages} onReorder={onReorderPages} className="flex flex-col mt-2">
                  {pages.map(page => (
                    <PageItem
                      key={page.id}
                      page={page}
                      pages={pages}
                      isActive={activePageId === page.id}
                      isHome={page.id === 'page-home'}
                      onSelect={onSelectPage}
                      onRename={onRenamePage}
                      onDuplicate={onDuplicatePage}
                      onDelete={onDeletePage}
                    />
                  ))}
                </Reorder.Group>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <p className="text-sm font-medium">No popups yet.</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PagesPanel;